import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Package, Truck, ClipboardCopy, Check, Plus, Search, X,
  ChevronDown, ChevronUp, Save, History, ShoppingCart, Settings2, Edit3, Clock, Trash2
} from "lucide-react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { AggiungiProdottoDialog, MessaggioDialog, TemplateDialog } from "../../components/acquisti/OrdiniFornitoriDialogs";
import { DateInput } from "../../components/DateInput";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { cn } from "../../lib/utils";

const API = process.env.REACT_APP_BACKEND_URL;

const OrdiniFornitori = ({ area = "food", onBack, showStorico = false, hideHeader = false }) => {
  const { user, token, localeCorrente, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [gruppi, setGruppi] = useState([]);
  const [storico, setStorico] = useState([]);
  const [ordiniInAttesa, setOrdiniInAttesa] = useState([]);
  const [tab, setTab] = useState(showStorico ? "storico" : "nuovi");
  const [fornitori, setFornitori] = useState([]);

  // Ordine in composizione
  const [ordineAperto, setOrdineAperto] = useState(null); // fornitore_id
  const [quantita, setQuantita] = useState({}); // {prodotto_id: qty}
  const [fornitoreMod, setFornitoriMod] = useState({}); // {prodotto_id: {fornitore_id, fornitore_nome}}
  const [saving, setSaving] = useState(false);

  // Stato unità ordine: "conf" | "kg" | "lt" | "pz" | "cartoni" | "buste" | "bottiglie" | "casse"
  const [unitaOrdine, setUnitaOrdine] = useState({});
  // Mostra/nascondi cambio fornitore per prodotto
  const [showFornitorePer, setShowFornitorePer] = useState({});

  // Dialog aggiunta manuale
  const [addDialog, setAddDialog] = useState(false);
  const [addSearch, setAddSearch] = useState("");
  const [addResults, setAddResults] = useState([]);
  const [itemsManuali, setItemsManuali] = useState([]); // items aggiunti manualmente

  // Dialog template messaggio
  const [templateDialog, setTemplateDialog] = useState(false);
  const [template, setTemplate] = useState("");

  // Dialog messaggio generato
  const [messaggioDialog, setMessaggioDialog] = useState(false);
  const [messaggioGenerato, setMessaggioGenerato] = useState("");

  // Storico dettaglio ordine
  const [storicoDettaglio, setStoricoDettaglio] = useState(null);
  // Storico: vista per fornitore / per prodotto + editing
  const [storicoView, setStoricoView] = useState("fornitore"); // "fornitore" | "prodotto"
  const [storicoSearch, setStoricoSearch] = useState("");
  const [prodottoAperto, setProdottoAperto] = useState(null); // prodotto_id espanso in vista prodotto
  const [editItemKey, setEditItemKey] = useState(null); // `${ordineId}:${prodottoId}`
  const [editQtyVal, setEditQtyVal] = useState("");
  const [editFornVal, setEditFornVal] = useState("");
  const [editDataVal, setEditDataVal] = useState(""); // YYYY-MM-DD
  const [confermaEliminaItemKey, setConfermaEliminaItemKey] = useState(null);
  const [fornitoreApertoStorico, setFornitoreApertoStorico] = useState(null); // fornitore_id espanso
  const [editDataOrdineId, setEditDataOrdineId] = useState(null); // ordine_id con editor data aperto
  // Filtro periodo storico
  const [periodo, setPeriodo] = useState("3mesi"); // mese | 3mesi | 6mesi | anno | custom
  const [dataDa, setDataDa] = useState("");
  const [dataA, setDataA] = useState("");

  // Ordini in attesa dettaglio (espandibile come storico)
  const [inAttesaDettaglio, setInAttesaDettaglio] = useState(null);
  // Cambio fornitore per ordine in attesa
  const [editFornitoreOrdineId, setEditFornitoreOrdineId] = useState(null);
  const [editFornitoreValue, setEditFornitoreValue] = useState(null); // {id, nome}
  // Conferma eliminazione ordine in attesa
  const [confermaEliminaId, setConfermaEliminaId] = useState(null);

  // Nuovo Ordine Manuale
  const [nuovoOrdineStep, setNuovoOrdineStep] = useState(null); // null | "seleziona" | "quantita"
  const [tuttiProdotti, setTuttiProdotti] = useState([]);
  const [prodottiSearch, setProdottiSearch] = useState("");
  const [prodottiSelezionati, setProdottiSelezionati] = useState([]); // [{id, nome, unita_misura, fornitore_id, fornitore_nome, confezionamento}]
  const [loadingProdotti, setLoadingProdotti] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };
  const areaLabel = area === "food" ? "Food & Beverage" : "Attrezzature & Packaging";

  const fetchGruppi = useCallback(async () => {
    if (!localeCorrente?.id || !token) return;
    try {
      const res = await axios.get(`${API}/api/ordini-fornitori/da-segnalazioni`, {
        headers, params: { locale_id: localeCorrente?.id, area }
      });
      setGruppi(res.data);
    } catch (err) {
      console.error(err);
    }
  }, [localeCorrente?.id, token, area]);

  const fetchStorico = useCallback(async () => {
    if (!localeCorrente?.id || !token) return;
    try {
      const res = await axios.get(`${API}/api/ordini-fornitori/storico`, {
        headers, params: { locale_id: localeCorrente?.id, area }
      });
      setOrdiniInAttesa(res.data.filter(o => o.stato === "da_ordinare"));
      setStorico(res.data.filter(o => o.stato === "ordinato"));
    } catch (err) {
      console.error(err);
    }
  }, [localeCorrente?.id, token, area]);

  const fetchFornitori = useCallback(async () => {
    if (!localeCorrente?.id || !token) return;
    try {
      const res = await axios.get(`${API}/api/ordini-fornitori/fornitori-disponibili`, {
        headers, params: { locale_id: localeCorrente?.id }
      });
      setFornitori(res.data);
    } catch (err) {
      console.error(err);
    }
  }, [localeCorrente?.id, token]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchGruppi(), fetchStorico(), fetchFornitori()]);
      setLoading(false);
    };
    load();
  }, [fetchGruppi, fetchStorico, fetchFornitori]);

  // Apri gruppo per composizione ordine
  const apriGruppo = (gruppo) => {
    setOrdineAperto(ordineAperto === gruppo.fornitore_id ? null : gruppo.fornitore_id);
    const qtyInit = {};
    const unitInit = {};
    gruppo.items.forEach(item => {
      qtyInit[item.prodotto_id] = item.quantita_suggerita || "";
      // Auto-seleziona unità: se conosco il confezionamento del fornitore, lo preseleziono
      if (item.confezionamento?.conf_tipo) {
        unitInit[item.prodotto_id] = "conf";
      } else {
        // Default: unità base del prodotto
        unitInit[item.prodotto_id] = item.unita_misura || "pz";
      }
    });
    setQuantita(qtyInit);
    setUnitaOrdine(unitInit);
    setFornitoriMod({});
    setShowFornitorePer({});
    setItemsManuali([]);
  };

  // Cerca prodotti per aggiunta manuale
  const searchProdotti = async (q) => {
    setAddSearch(q);
    if (q.length < 2) { setAddResults([]); return; }
    try {
      const res = await axios.get(`${API}/api/ordini-fornitori/prodotti-disponibili`, {
        headers, params: { locale_id: localeCorrente?.id, area, search: q }
      });
      setAddResults(res.data);
    } catch { }
  };

  const aggiungiProdottoManuale = (prodotto) => {
    // Evita duplicati
    if (itemsManuali.find(i => i.prodotto_id === prodotto.id)) return;
    const gruppoCorrente = gruppi.find(g => g.fornitore_id === ordineAperto);
    if (gruppoCorrente?.items.find(i => i.prodotto_id === prodotto.id)) return;

    setItemsManuali(prev => [...prev, {
      prodotto_id: prodotto.id,
      prodotto_nome: prodotto.nome,
      unita_misura: prodotto.unita_misura || "pz",
      alert_id: null,
      note: null,
    }]);
    setQuantita(prev => ({ ...prev, [prodotto.id]: "" }));
    setAddDialog(false);
    setAddSearch("");
    setAddResults([]);
  };

  // Salva ordine
  const salvaOrdine = async (gruppo) => {
    if (!localeCorrente?.id) {
      toast.error("Seleziona prima un locale per creare un ordine");
      return;
    }
    setSaving(true);
    try {
      const allItems = [];
      const CUSTOM_UNITS = { cartoni: "Cartoni", buste: "Buste", bottiglie: "Bottiglie", casse: "Casse" };

      // Items dalle segnalazioni
      for (const item of gruppo.items) {
        const mod = fornitoreMod[item.prodotto_id];
        if (mod && mod.fornitore_id !== gruppo.fornitore_id) continue;
        const conf = item.confezionamento;
        const unit = unitaOrdine[item.prodotto_id] || item.unita_misura;
        const rawQty = quantita[item.prodotto_id] ? parseFloat(quantita[item.prodotto_id]) : null;

        if (unit === "conf" && conf?.conf_qtà_unitaria) {
          // Unità confezionamento nota: conversione
          const qtyBase = rawQty ? rawQty * conf.conf_qtà_unitaria : null;
          allItems.push({
            prodotto_id: item.prodotto_id, prodotto_nome: item.prodotto_nome, tipo: area,
            quantita: qtyBase ? parseFloat(qtyBase.toFixed(2)) : null,
            unita_misura: conf.conf_um,
            quantita_conf: rawQty, conf_tipo: conf.conf_tipo, conf_desc: conf.conf_desc,
            alert_id: item.alert_id, note: item.note,
          });
        } else if (CUSTOM_UNITS[unit]) {
          // Unità personalizzata (cartoni/buste/bottiglie/casse) senza conversione
          allItems.push({
            prodotto_id: item.prodotto_id, prodotto_nome: item.prodotto_nome, tipo: area,
            quantita: null, unita_misura: item.unita_misura,
            quantita_conf: rawQty, conf_tipo: CUSTOM_UNITS[unit], conf_desc: null,
            alert_id: item.alert_id, note: item.note,
          });
        } else {
          // Unità base (kg/lt/pz)
          allItems.push({
            prodotto_id: item.prodotto_id, prodotto_nome: item.prodotto_nome, tipo: area,
            quantita: rawQty, unita_misura: unit || item.unita_misura,
            quantita_conf: null, conf_tipo: null, conf_desc: null,
            alert_id: item.alert_id, note: item.note,
          });
        }
      }

      // Items aggiunti manualmente
      for (const item of itemsManuali) {
        const unit = unitaOrdine[item.prodotto_id] || item.unita_misura;
        const rawQty = quantita[item.prodotto_id] ? parseFloat(quantita[item.prodotto_id]) : null;
        if (CUSTOM_UNITS[unit]) {
          allItems.push({
            prodotto_id: item.prodotto_id, prodotto_nome: item.prodotto_nome, tipo: area,
            quantita: null, unita_misura: item.unita_misura,
            quantita_conf: rawQty, conf_tipo: CUSTOM_UNITS[unit], conf_desc: null,
            alert_id: null, note: null,
          });
        } else {
          allItems.push({
            prodotto_id: item.prodotto_id, prodotto_nome: item.prodotto_nome, tipo: area,
            quantita: rawQty, unita_misura: unit || item.unita_misura,
            alert_id: null, note: null,
          });
        }
      }

      if (allItems.length === 0) {
        toast.error("Nessun prodotto nell'ordine");
        setSaving(false);
        return;
      }

      const userId = user?.id || user?.operatore_id || "";
      const userNome = user?.nome || user?.email || user?.telefono || "";

      const res = await axios.post(`${API}/api/ordini-fornitori`, {
        locale_id: localeCorrente?.id,
        fornitore_id: gruppo.fornitore_id || "senza_fornitore",
        fornitore_nome: gruppo.fornitore_nome,
        area,
        items: allItems,
      }, {
        headers,
        params: { user_id: userId, user_nome: userNome }
      });

      toast.success("Ordine salvato in 'Da ordinare'! Premi 'Ordinato' quando lo hai inviato al fornitore.");

      // Salva preferenze unità di misura
      const prefs = {};
      [...(gruppo.items || []), ...(itemsManuali || [])].forEach(item => {
        if (unitaOrdine[item.prodotto_id]) prefs[item.prodotto_id] = unitaOrdine[item.prodotto_id];
      });
      if (Object.keys(prefs).length > 0) {
        axios.put(`${API}/api/ordini-fornitori/preferenze-unita`, {
          locale_id: localeCorrente?.id,
          preferenze: prefs,
        }, { headers }).catch(() => {});
      }

      // Refresh
      await Promise.all([fetchGruppi(), fetchStorico()]);
      setOrdineAperto(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore salvataggio ordine");
    }
    setSaving(false);
  };

  // Copia messaggio con fallback per ambienti senza Clipboard API
  const copiaMessaggio = (msg) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(msg)
        .then(() => toast.success("Messaggio copiato!"))
        .catch(() => fallbackCopy(msg));
    } else {
      fallbackCopy(msg);
    }
  };

  const fallbackCopy = (msg) => {
    const el = document.createElement("textarea");
    el.value = msg;
    el.style.cssText = "position:fixed;opacity:0;pointer-events:none;";
    document.body.appendChild(el);
    el.select();
    try {
      document.execCommand("copy");
      toast.success("Messaggio copiato!");
    } catch {
      toast.error("Impossibile copiare automaticamente — copia il testo manualmente");
    }
    document.body.removeChild(el);
  };

  // Rigenera messaggio per ordine storico
  const rigeneraMessaggio = async (ordine) => {
    try {
      const userNome = user?.nome || user?.email || user?.telefono || "";
      const res = await axios.get(`${API}/api/ordini-fornitori/${ordine.id}/messaggio`, {
        headers, params: { user_nome: userNome }
      });
      setMessaggioGenerato(res.data.messaggio);
      setMessaggioDialog(true);
    } catch { toast.error("Errore generazione messaggio"); }
  };

  // Segna un ordine "da_ordinare" come ordinato
  const segnaOrdinato = async (ordine) => {
    const userId = user?.id || user?.operatore_id || "";
    const userNome = user?.nome || user?.email || user?.telefono || "";
    try {
      await axios.put(
        `${API}/api/ordini-fornitori/${ordine.id}/ordinato`,
        null,
        { headers, params: { user_id: userId, user_nome: userNome } }
      );
      toast.success("Ordine completato e spostato nello Storico!");
      setInAttesaDettaglio(null);
      await Promise.all([fetchGruppi(), fetchStorico()]);
      setTab("storico");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore aggiornamento ordine");
    }
  };

  // Aggiorna la data di un ordine (da ordinare o storico)
  const aggiornaDataOrdine = async (ordineId, dataISO) => {
    if (!dataISO) return;
    try {
      await axios.patch(`${API}/api/ordini-fornitori/${ordineId}/data`, { data: dataISO }, { headers });
      toast.success("Data aggiornata!");
      setEditDataOrdineId(null);
      await fetchStorico();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore modifica data");
    }
  };

  // Aggiorna il fornitore di un ordine in attesa
  const aggiornaFornitoreOrdine = async (ordineId, fornitoreId, fornitoreNome) => {
    try {
      await axios.patch(
        `${API}/api/ordini-fornitori/${ordineId}/fornitore`,
        { fornitore_id: fornitoreId || null, fornitore_nome: fornitoreNome || "Senza fornitore" },
        { headers }
      );
      toast.success("Fornitore aggiornato!");
      setEditFornitoreOrdineId(null);
      setEditFornitoreValue(null);
      await fetchStorico();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore aggiornamento fornitore");
    }
  };

  // Elimina un ordine in attesa
  const eliminaOrdine = async (ordineId) => {
    try {
      await axios.delete(`${API}/api/ordini-fornitori/${ordineId}`, { headers });
      toast.success("Ordine eliminato.");
      setInAttesaDettaglio(null);
      setConfermaEliminaId(null);
      await fetchStorico();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore eliminazione ordine");
    }
  };

  // Config template
  const apriTemplate = async () => {
    try {
      const res = await axios.get(`${API}/api/ordini-fornitori/template/${area}`, {
        headers, params: { locale_id: localeCorrente?.id }
      });
      setTemplate(res.data.template);
      setTemplateDialog(true);
    } catch { toast.error("Errore caricamento template"); }
  };

  const salvaTemplate = async () => {
    try {
      await axios.put(`${API}/api/ordini-fornitori/template/${area}`, { template }, {
        headers, params: { locale_id: localeCorrente?.id }
      });
      toast.success("Template salvato!");
      setTemplateDialog(false);
    } catch { toast.error("Errore salvataggio template"); }
  };

  const formatDate = (iso) => {
    if (!iso) return "-";
    try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso.slice(0, 10); }
  };

  const formatItemQty = (item) => {
    if (item.quantita_conf != null && item.conf_tipo) return `${item.quantita_conf} ${item.conf_tipo}`;
    if (item.quantita != null) return `${item.quantita} ${item.unita_misura || "pz"}`;
    return "-";
  };

  // Salva modifiche (quantità + data + eventuale cambio fornitore) su un prodotto ordinato
  const salvaModificheItem = async (ordine, item) => {
    const hasConf = item.quantita_conf != null && item.conf_tipo;
    const q = parseFloat(editQtyVal);
    if (isNaN(q) || q <= 0) { toast.error("Inserisci una quantità valida"); return; }
    try {
      const body = hasConf ? { quantita_conf: q } : { quantita: q };
      await axios.patch(`${API}/api/ordini-fornitori/${ordine.id}/item/${item.prodotto_id}`, body, { headers });
      // Data (solo vista per prodotto)
      const currentDate = (ordine.ordinato_at || ordine.created_at || "").slice(0, 10);
      if (editDataVal && editDataVal !== currentDate) {
        await axios.patch(`${API}/api/ordini-fornitori/${ordine.id}/data`, { data: editDataVal }, { headers });
      }
      // Fornitore
      const nuovoForn = editFornVal || "";
      if (nuovoForn !== (ordine.fornitore_id || "")) {
        const f = fornitori.find(x => x.id === nuovoForn);
        await axios.post(`${API}/api/ordini-fornitori/${ordine.id}/sposta-prodotto`, {
          prodotto_id: item.prodotto_id,
          fornitore_id: f ? f.id : null,
          fornitore_nome: f ? f.nome : "Senza fornitore",
        }, { headers });
      }
      toast.success("Prodotto aggiornato!");
      setEditItemKey(null);
      await fetchStorico();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore aggiornamento prodotto");
    }
  };

  // Elimina una singola riga prodotto da un ordine
  const eliminaItemStorico = async (ordine, item) => {
    try {
      await axios.delete(`${API}/api/ordini-fornitori/${ordine.id}/item/${item.prodotto_id}`, { headers });
      toast.success("Riga eliminata");
      setConfermaEliminaItemKey(null);
      setEditItemKey(null);
      await fetchStorico();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore eliminazione");
    }
  };

  // Riga prodotto editabile. showFornitoreInfo=true → vista "Per prodotto" (data in evidenza + modifica data)
  const renderStoricoItem = (ordine, item, idx, showFornitoreInfo) => {
    const key = `${ordine.id}:${item.prodotto_id}`;
    const isEditing = editItemKey === key;
    const isConfermaElimina = confermaEliminaItemKey === key;
    const hasConf = item.quantita_conf != null && item.conf_tipo;
    const dataOrdine = ordine.ordinato_at || ordine.created_at;
    return (
      <div key={key + idx} className="py-2.5 border-b border-border last:border-0" data-testid={`storico-item-${ordine.id}-${item.prodotto_id}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            {showFornitoreInfo ? (
              <>
                {/* 1. QUANDO (in evidenza) */}
                <span className="text-sm font-bold text-foreground">{formatDate(dataOrdine)}</span>
                {/* 3. DA CHI */}
                <div className="flex items-center gap-1 mt-0.5">
                  <Truck className="h-3 w-3 text-muted-foreground shrink-0" />
                  <span className="text-xs text-muted-foreground">{ordine.fornitore_nome || "Senza fornitore"}</span>
                </div>
              </>
            ) : (
              <span className="text-sm text-foreground block truncate">{item.prodotto_nome}</span>
            )}
          </div>
          {!isEditing && (
            <div className="flex items-center gap-1.5 shrink-0">
              {/* 2. QUANTO */}
              <span className="text-sm text-foreground font-bold mr-1">{formatItemQty(item)}</span>
              <button
                onClick={() => { setEditItemKey(key); setConfermaEliminaItemKey(null); setEditQtyVal(hasConf ? item.quantita_conf : (item.quantita ?? "")); setEditFornVal(ordine.fornitore_id || ""); setEditDataVal((dataOrdine || "").slice(0, 10)); }}
                className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                data-testid={`edit-item-btn-${ordine.id}-${item.prodotto_id}`}
              >
                <Edit3 className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => setConfermaEliminaItemKey(isConfermaElimina ? null : key)}
                className="p-1.5 rounded-lg hover:bg-red-500/10 text-muted-foreground hover:text-red-500 transition-colors"
                data-testid={`delete-item-btn-${ordine.id}-${item.prodotto_id}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>

        {/* Conferma eliminazione inline */}
        {isConfermaElimina && !isEditing && (
          <div className="flex items-center gap-2 mt-2 p-2.5 rounded-lg bg-red-500/10 border border-red-500/20">
            <span className="text-xs text-red-600 flex-1 font-medium">Eliminare questo ordine?</span>
            <Button variant="ghost" size="sm" className="h-7 px-3 text-xs" onClick={() => setConfermaEliminaItemKey(null)}>Annulla</Button>
            <Button size="sm" className="h-7 px-3 text-xs bg-red-500 hover:bg-red-600 text-white"
              onClick={() => eliminaItemStorico(ordine, item)}
              data-testid={`conferma-delete-item-${ordine.id}-${item.prodotto_id}`}
            >Sì, elimina</Button>
          </div>
        )}

        {isEditing && (
          <div className="mt-2 space-y-2 p-2.5 rounded-lg bg-muted/40 border border-border">
            <div className="space-y-1">
              <p className="text-[11px] text-muted-foreground">Quantità:</p>
              <div className="flex items-center gap-2">
                <Input
                  type="number" step="any" inputMode="decimal"
                  value={editQtyVal}
                  onChange={e => setEditQtyVal(e.target.value)}
                  className="h-9 flex-1 text-base"
                  data-testid={`edit-qty-input-${ordine.id}-${item.prodotto_id}`}
                />
                <span className="text-xs text-muted-foreground min-w-[45px] text-center">{hasConf ? item.conf_tipo : (item.unita_misura || "pz")}</span>
              </div>
            </div>
            {showFornitoreInfo && (
              <div className="space-y-1">
                <p className="text-[11px] text-muted-foreground">Data ordine:</p>
                <DateInput value={editDataVal} onChange={setEditDataVal} />
              </div>
            )}
            <div className="space-y-1">
              <p className="text-[11px] text-muted-foreground">Fornitore:</p>
              <select
                value={editFornVal}
                onChange={e => setEditFornVal(e.target.value)}
                className="h-9 w-full text-xs rounded-lg border border-border bg-card px-2"
                data-testid={`edit-forn-select-${ordine.id}-${item.prodotto_id}`}
              >
                <option value="">Senza fornitore</option>
                {fornitori.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
              </select>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" className="flex-1" onClick={() => setEditItemKey(null)}>Annulla</Button>
              <Button
                size="sm" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => salvaModificheItem(ordine, item)}
                data-testid={`save-item-btn-${ordine.id}-${item.prodotto_id}`}
              >
                <Save className="h-3.5 w-3.5 mr-1" />Salva
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Range date dal filtro periodo
  const getRangeDates = () => {
    const now = new Date();
    if (periodo === "custom") {
      return {
        da: dataDa ? new Date(dataDa + "T00:00:00") : null,
        a: dataA ? new Date(dataA + "T23:59:59") : null,
      };
    }
    const da = new Date(now);
    if (periodo === "mese") da.setMonth(da.getMonth() - 1);
    else if (periodo === "3mesi") da.setMonth(da.getMonth() - 3);
    else if (periodo === "6mesi") da.setMonth(da.getMonth() - 6);
    else if (periodo === "anno") da.setFullYear(da.getFullYear() - 1);
    return { da, a: now };
  };

  // Ordini COMPLETATI (storico) filtrati per periodo
  const ordiniCompletati = useMemo(() => {
    const { da, a } = getRangeDates();
    return storico.filter(o => {
      const d = new Date(o.ordinato_at || o.created_at);
      if (da && d < da) return false;
      if (a && d > a) return false;
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storico, periodo, dataDa, dataA]);

  // Raggruppa per fornitore (con frequenza ordini)
  const fornitoriAggregati = useMemo(() => {
    const map = {};
    ordiniCompletati.forEach(o => {
      const fid = o.fornitore_id || "senza";
      if (!map[fid]) map[fid] = { fornitore_id: o.fornitore_id, fornitore_nome: o.fornitore_nome || "Senza fornitore", ordini: [] };
      map[fid].ordini.push(o);
    });
    const arr = Object.values(map);
    arr.forEach(g => {
      g.ordini.sort((a, b) => (b.ordinato_at || b.created_at || "").localeCompare(a.ordinato_at || a.created_at || ""));
      if (g.ordini.length >= 2) {
        const ds = g.ordini.map(o => new Date(o.ordinato_at || o.created_at)).sort((a, b) => a - b);
        const spanDays = (ds[ds.length - 1] - ds[0]) / 86400000;
        g.frequenzaGiorni = Math.max(1, Math.round(spanDays / (g.ordini.length - 1)));
      } else g.frequenzaGiorni = null;
    });
    arr.sort((a, b) => a.fornitore_nome.localeCompare(b.fornitore_nome));
    return arr;
  }, [ordiniCompletati]);

  // Aggrega gli ordini completati per prodotto (con totali per unità)
  const prodottiAggregati = useMemo(() => {
    const map = {};
    ordiniCompletati.forEach(ordine => {
      (ordine.items || []).forEach(item => {
        const pid = item.prodotto_id;
        if (!map[pid]) map[pid] = { prodotto_id: pid, prodotto_nome: item.prodotto_nome, entries: [], totali: {} };
        map[pid].entries.push({ ordine, item });
        let val = null, unit = null;
        if (item.quantita != null) { val = item.quantita; unit = item.unita_misura || "pz"; }
        else if (item.quantita_conf != null) { val = item.quantita_conf; unit = item.conf_tipo || "pz"; }
        if (val != null) map[pid].totali[unit] = (map[pid].totali[unit] || 0) + val;
      });
    });
    const arr = Object.values(map);
    arr.forEach(p => p.entries.sort((a, b) => {
      const da = a.ordine.ordinato_at || a.ordine.created_at || "";
      const dbb = b.ordine.ordinato_at || b.ordine.created_at || "";
      return dbb.localeCompare(da);
    }));
    arr.sort((a, b) => (a.prodotto_nome || "").localeCompare(b.prodotto_nome || ""));
    return arr;
  }, [ordiniCompletati]);

  const formatTotali = (totali) => {
    const parts = Object.entries(totali).map(([u, v]) => `${Math.round(v * 100) / 100} ${u}`);
    return parts.length ? parts.join(" · ") : "-";
  };

  // Aggrega le quantità per mese (per il mini-grafico andamento)
  const MESI_SHORT = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];
  const buildMensili = (entries) => {
    const map = {};
    entries.forEach(({ ordine, item }) => {
      const d = new Date(ordine.ordinato_at || ordine.created_at);
      if (isNaN(d)) return;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const val = item.quantita != null ? item.quantita : (item.quantita_conf != null ? item.quantita_conf : 0);
      map[key] = (map[key] || 0) + val;
    });
    return Object.entries(map)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([k, v]) => ({ mese: k, val: Math.round(v * 100) / 100 }));
  };

  // ===== NUOVO ORDINE MANUALE =====
  const avviaNuovoOrdine = async () => {
    setNuovoOrdineStep("seleziona");
    setProdottiSelezionati([]);
    setProdottiSearch("");
    setFornitoriMod({});
    setLoadingProdotti(true);
    try {
      const [prodRes, prefRes] = await Promise.all([
        axios.get(`${API}/api/ordini-fornitori/prodotti-con-fornitore`, { headers, params: { locale_id: localeCorrente?.id, area } }),
        axios.get(`${API}/api/ordini-fornitori/preferenze-unita`, { headers, params: { locale_id: localeCorrente?.id } }).catch(() => ({ data: {} }))
      ]);
      setTuttiProdotti(prodRes.data);
      // Pre-carica preferenze unità salvate
      if (prefRes.data && typeof prefRes.data === 'object') {
        setUnitaOrdine(prev => ({ ...prev, ...prefRes.data }));
      }
    } catch { toast.error("Errore caricamento prodotti"); }
    finally { setLoadingProdotti(false); }
  };

  const toggleProdottoSelezionato = (p) => {
    setProdottiSelezionati(prev => {
      const exists = prev.find(x => x.id === p.id);
      if (exists) return prev.filter(x => x.id !== p.id);
      return [...prev, p];
    });
  };

  const passaAQuantita = () => {
    if (prodottiSelezionati.length === 0) { toast.error("Seleziona almeno un prodotto"); return; }
    setNuovoOrdineStep("quantita");
  };

  const salvaOrdineManuale = async () => {
    // Raggruppa per fornitore
    const gruppiManuale = {};
    prodottiSelezionati.forEach(p => {
      const ovr = fornitoreMod[p.id];
      const fId = ovr ? (ovr.fornitore_id || "senza_fornitore") : (p.fornitore_id || "senza_fornitore");
      const fNome = ovr ? (ovr.fornitore_nome || "Senza fornitore") : (p.fornitore_nome || "Senza fornitore");
      if (!gruppiManuale[fId]) gruppiManuale[fId] = { fornitore_id: fId, fornitore_nome: fNome, items: [] };
      const unit = unitaOrdine[p.id] || p.unita_misura || "pz";
      gruppiManuale[fId].items.push({
        prodotto_id: p.id,
        prodotto_nome: p.nome,
        tipo: area,
        quantita: parseFloat(quantita[p.id]) || 0,
        unita_misura: unit,
      });
    });

    setSaving(true);
    try {
      const userId = user?.id || user?.operatore_id || "";
      const userNome = user?.nome || user?.email || user?.telefono || "Operatore";
      let ordiniCreati = 0;
      for (const g of Object.values(gruppiManuale)) {
        const validItems = g.items.filter(i => i.quantita > 0);
        if (validItems.length === 0) continue;
        const payload = {
          locale_id: localeCorrente?.id,
          fornitore_id: g.fornitore_id,
          fornitore_nome: g.fornitore_nome,
          area,
          items: validItems,
        };
        await axios.post(`${API}/api/ordini-fornitori`, payload, {
          headers,
          params: { user_id: userId, user_nome: userNome }
        });
        ordiniCreati++;
      }
      if (ordiniCreati > 0) {
        toast.success(`${ordiniCreati > 1 ? ordiniCreati + " ordini salvati" : "Ordine salvato"} in "Da ordinare"! Premi "Ordinato" quando lo hai inviato al fornitore.`);
      } else {
        toast.error("Nessun prodotto con quantità valida");
      }
      setNuovoOrdineStep(null);
      setProdottiSelezionati([]);
      // Salva preferenze unità
      const prefs = {};
      prodottiSelezionati.forEach(p => {
        if (unitaOrdine[p.id]) prefs[p.id] = unitaOrdine[p.id];
      });
      if (Object.keys(prefs).length > 0) {
        axios.put(`${API}/api/ordini-fornitori/preferenze-unita`, {
          locale_id: localeCorrente?.id,
          preferenze: prefs,
        }, { headers }).catch(() => {});
      }
      setQuantita({});
      setUnitaOrdine({});
      await Promise.all([fetchGruppi(), fetchStorico()]);
    } catch(err) {
      console.error("Errore salvataggio:", err?.response?.data);
      toast.error(err.response?.data?.detail || "Errore salvataggio ordine");
    }
    finally { setSaving(false); }
  };

  const prodottiFiltrati = tuttiProdotti.filter(p =>
    !prodottiSearch || p.nome.toLowerCase().includes(prodottiSearch.toLowerCase())
  );

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;

  return (
    <div className="space-y-4">
      {/* Header interno — nascosto se siamo dentro OrdiniPage (hideHeader) */}
      {!hideHeader && (
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {onBack && <Button variant="ghost" size="icon" onClick={onBack} data-testid="ordini-back-btn"><ArrowLeft className="h-5 w-5" /></Button>}
          <div>
            <h2 className="text-lg font-bold text-foreground" data-testid="ordini-title">Ordini {areaLabel}</h2>
            <p className="text-xs text-muted-foreground">Gestisci ordini raggruppati per fornitore</p>
          </div>
        </div>
        {isAdmin && (
          <Button variant="outline" size="sm" onClick={apriTemplate} data-testid="template-btn">
            <Settings2 className="h-3.5 w-3.5 mr-1.5" />Template messaggio
          </Button>
        )}
      </div>
      )}

      {/* Pulsante Template — visibile sempre ma in riga con i tabs quando hideHeader */}
      {hideHeader && isAdmin && (
        <div className="flex justify-end">
          <button
            onClick={apriTemplate}
            className="h-9 px-4 rounded-xl border border-border bg-muted hover:bg-muted/80 text-muted-foreground text-sm flex items-center gap-1.5 transition-all"
            data-testid="template-btn"
          >
            <Settings2 className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Template messaggio</span>
          </button>
        </div>
      )}

      {/* Tabs + Nuovo Ordine */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setTab("nuovi")}
          data-testid="tab-nuovi"
          className={`h-11 px-5 rounded-2xl font-semibold flex items-center gap-2 text-sm transition-all ${
            tab === "nuovi"
              ? "bg-primary text-white"
              : "bg-muted text-muted-foreground hover:bg-muted/80 border border-border"
          }`}
        >
          <ShoppingCart className="h-3.5 w-3.5" />
          Da ordinare ({ordiniInAttesa.length + gruppi.reduce((acc, g) => acc + g.items.length, 0)})
        </button>
        <button
          onClick={() => setTab("storico")}
          data-testid="tab-storico"
          className={`h-11 px-5 rounded-2xl font-semibold flex items-center gap-2 text-sm transition-all ${
            tab === "storico"
              ? "bg-primary text-white"
              : "bg-muted text-muted-foreground hover:bg-muted/80 border border-border"
          }`}
        >
          <History className="h-3.5 w-3.5" />
          Storico
        </button>
        <button
          onClick={avviaNuovoOrdine}
          className="h-11 px-5 bg-primary text-white rounded-2xl font-bold flex items-center gap-2 text-sm active:scale-[0.98] transition-all ml-auto"
          data-testid="nuovo-ordine-btn"
        >
          <Plus className="h-4 w-4" />
          <span className="hidden sm:inline">Nuovo</span>
        </button>
      </div>

      {/* TAB: Da ordinare */}
      {tab === "nuovi" && (
        <div className="space-y-3">
          {/* Sezione: Ordini salvati in attesa di conferma */}
          {ordiniInAttesa.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 px-1">
                <Clock className="h-3.5 w-3.5 text-amber-500" />
                <span className="text-xs font-semibold text-amber-600 uppercase tracking-wide">Ordini in attesa di conferma</span>
              </div>
              {ordiniInAttesa.map(ordine => (
                <div key={ordine.id} className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden" data-testid={`ordine-in-attesa-${ordine.id}`}>
                  {/* Header espandibile */}
                  <div
                    className="p-4 cursor-pointer hover:bg-white/10 transition-colors"
                    onClick={() => {
                      setInAttesaDettaglio(inAttesaDettaglio === ordine.id ? null : ordine.id);
                      if (editFornitoreOrdineId === ordine.id) { setEditFornitoreOrdineId(null); setEditFornitoreValue(null); }
                    }}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <Truck className="h-4 w-4 text-amber-500" />
                        <span className="font-bold text-sm">{ordine.fornitore_nome || "Senza fornitore"}</span>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600">
                          IN ATTESA
                        </span>
                      </div>
                      {inAttesaDettaglio === ordine.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span>{ordine.items?.length || 0} prodotti</span>
                      <span>·</span>
                      <span>{formatDate(ordine.created_at)}</span>
                      {ordine.creato_da_nome && <><span>·</span><span>da {ordine.creato_da_nome}</span></>}
                    </div>
                  </div>

                  {/* Dettaglio espanso */}
                  {inAttesaDettaglio === ordine.id && (
                    <div className="px-4 pb-4 space-y-3">
                      {/* Lista prodotti */}
                      <div className="space-y-0">
                        {ordine.items?.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between text-sm py-1.5 border-b border-border/50 last:border-0">
                            <span className="text-foreground">{item.prodotto_nome}</span>
                            <span className="text-muted-foreground font-medium">
                              {item.quantita_conf && item.conf_tipo
                                ? `${item.quantita_conf} ${item.conf_tipo}`
                                : item.quantita
                                  ? `${item.quantita} ${item.unita_misura || "pz"}`
                                  : "-"
                              }
                            </span>
                          </div>
                        ))}
                      </div>

                      {/* Cambio fornitore */}
                      <div className="pt-1 space-y-1.5">
                        <p className="text-xs font-medium text-muted-foreground">Fornitore ordine:</p>
                        <div className="flex gap-2 items-center">
                          <select
                            className="flex-1 h-9 text-xs rounded-lg border border-border bg-card px-2"
                            value={editFornitoreOrdineId === ordine.id
                              ? editFornitoreValue?.id || ""
                              : ordine.fornitore_id || ""}
                            onChange={e => {
                              setEditFornitoreOrdineId(ordine.id);
                              const f = fornitori.find(f => f.id === e.target.value);
                              setEditFornitoreValue(f ? { id: f.id, nome: f.nome } : { id: null, nome: "Senza fornitore" });
                            }}
                            data-testid={`fornitore-select-attesa-${ordine.id}`}
                          >
                            <option value="">Senza fornitore</option>
                            {fornitori.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
                          </select>
                          {editFornitoreOrdineId === ordine.id && (
                            <Button
                              size="sm" variant="outline"
                              onClick={() => aggiornaFornitoreOrdine(ordine.id, editFornitoreValue?.id, editFornitoreValue?.nome)}
                              data-testid={`salva-fornitore-${ordine.id}`}
                            >
                              Aggiorna
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Modifica data ordine */}
                      <div className="pt-1 space-y-1.5">
                        <p className="text-xs font-medium text-muted-foreground">Data ordine:</p>
                        <DateInput
                          value={(ordine.created_at || "").slice(0, 10)}
                          onChange={(d) => aggiornaDataOrdine(ordine.id, d)}
                        />
                      </div>

                      {/* Bottoni: Copia Messaggio + Completato */}
                      <div className="flex gap-2 pt-1">
                        <Button
                          variant="outline" size="sm" className="flex-1"
                          onClick={() => rigeneraMessaggio(ordine)}
                          data-testid={`copia-msg-attesa-${ordine.id}`}
                        >
                          <ClipboardCopy className="h-3.5 w-3.5 mr-1.5" />Copia messaggio
                        </Button>
                        <Button
                          size="sm" className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => segnaOrdinato(ordine)}
                          data-testid={`btn-ordinato-${ordine.id}`}
                        >
                          <Check className="h-3.5 w-3.5 mr-1" />Completato
                        </Button>
                      </div>
                      {/* Elimina ordine con conferma inline */}
                      {confermaEliminaId === ordine.id ? (
                        <div className="flex items-center gap-2 mt-1 p-2.5 rounded-lg bg-red-500/10 border border-red-500/20">
                          <span className="text-xs text-red-600 flex-1 font-medium">Eliminare questo ordine?</span>
                          <Button
                            variant="ghost" size="sm"
                            className="h-7 px-3 text-xs text-muted-foreground hover:bg-muted"
                            onClick={() => setConfermaEliminaId(null)}
                          >
                            Annulla
                          </Button>
                          <Button
                            size="sm"
                            className="h-7 px-3 text-xs bg-red-500 hover:bg-red-600 text-white"
                            onClick={() => eliminaOrdine(ordine.id)}
                            data-testid={`btn-conferma-elimina-${ordine.id}`}
                          >
                            Sì, elimina
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="ghost" size="sm"
                          className="w-full text-red-500 hover:text-red-600 hover:bg-red-500/10 mt-1"
                          onClick={() => setConfermaEliminaId(ordine.id)}
                          data-testid={`btn-elimina-ordine-${ordine.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-1.5" />Elimina ordine
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {gruppi.length > 0 && <div className="border-t border-white/10 pt-2 mt-2" />}
            </div>
          )}

          {/* Sezione: Segnalazioni attive */}
          {gruppi.length === 0 && ordiniInAttesa.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 bg-white/5 rounded-2xl border border-white/10">
              <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mb-4">
                <ShoppingCart className="h-8 w-8 text-white/30" />
              </div>
              <p className="text-white/50 text-sm">Nessuna segnalazione attiva</p>
            </div>
          ) : gruppi.length > 0 && (
            <div className="space-y-3">
              {ordiniInAttesa.length > 0 && (
                <div className="flex items-center gap-2 px-1">
                  <ShoppingCart className="h-3.5 w-3.5 text-blue-500" />
                  <span className="text-xs font-semibold text-blue-600 uppercase tracking-wide">Segnalazioni attive</span>
                </div>
              )}
              {gruppi.map(gruppo => (
            <div key={gruppo.fornitore_id || "none"} className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden">
              <div
                className={cn("py-3 px-4 cursor-pointer hover:bg-white/10 transition-colors", ordineAperto === gruppo.fornitore_id && "bg-white/10")}
                onClick={() => apriGruppo(gruppo)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-lime-400/20 rounded-xl flex items-center justify-center">
                      <Truck className="h-4 w-4 text-lime-400" />
                    </div>
                    <div>
                      <h3 className="font-bold text-sm text-white" data-testid={`gruppo-fornitore-${gruppo.fornitore_id}`}>
                        Ordine per {gruppo.fornitore_nome}
                      </h3>
                      <p className="text-xs text-white/50">{gruppo.items.length} prodotti da ordinare</p>
                    </div>
                  </div>
                  {ordineAperto === gruppo.fornitore_id ? <ChevronUp className="h-4 w-4 text-white/50" /> : <ChevronDown className="h-4 w-4 text-white/50" />}
                </div>
              </div>

              {ordineAperto === gruppo.fornitore_id && (
                <div className="pt-0 pb-4 px-3 space-y-2 border-t border-white/10">
                  {/* Items dalle segnalazioni */}
                  {gruppo.items.map(item => {
                    const conf = item.confezionamento;
                    const unit = unitaOrdine[item.prodotto_id] || "pz";
                    const qtyValue = quantita[item.prodotto_id] || "";
                    const hasConf = !!conf?.conf_tipo;
                    // Calcolo equivalenza kg/lt quando uso conf
                    const kgCalc = unit === "conf" && conf?.conf_qtà_unitaria && qtyValue
                      ? (parseFloat(qtyValue) * conf.conf_qtà_unitaria).toFixed(2) : null;

                    // Label dell'unità selezionata per il placeholder
                    const unitLabel = unit === "conf" ? conf?.conf_tipo : unit;

                    return (
                      <div key={item.prodotto_id} className="p-3 rounded-xl bg-muted/20 border border-border space-y-2" data-testid={`item-${item.prodotto_id}`}>
                        {/* Riga 1: Nome prodotto + urgenza */}
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-sm text-foreground leading-tight">{item.prodotto_nome}</span>
                          {item.urgenza === "urgente" && (
                            <span className="text-[10px] font-bold text-red-500 bg-red-500/10 px-2 py-0.5 rounded-full shrink-0 ml-2">URGENTE</span>
                          )}
                        </div>

                        {/* Riga 2: Input quantità + unità selezionata */}
                        <div className="flex items-center gap-2">
                          <Input
                            type="number" step="any" inputMode="decimal"
                            placeholder={item.quantita_suggerita ? `${item.quantita_suggerita}` : "Qtà"}
                            value={qtyValue}
                            onChange={e => setQuantita(prev => ({ ...prev, [item.prodotto_id]: e.target.value }))}
                            className="h-10 text-base flex-1 font-medium"
                            data-testid={`qty-${item.prodotto_id}`}
                          />
                          <span className="text-sm font-medium text-muted-foreground min-w-[50px] text-center">
                            {unit === "conf" ? (conf?.conf_tipo || "CT") : unitLabel}
                          </span>
                        </div>

                        {/* Riga 3: Pillole unità */}
                        <div className="flex flex-wrap gap-1.5">
                          {hasConf ? (
                            <>
                              {/* Se conosco il confezionamento: conf + base */}
                              <button
                                onClick={() => setUnitaOrdine(prev => ({ ...prev, [item.prodotto_id]: "conf" }))}
                                className={cn(
                                  "px-3 py-1.5 rounded-full text-xs font-medium transition-colors",
                                  unit === "conf"
                                    ? "bg-blue-600 text-white"
                                    : "bg-muted/50 text-muted-foreground hover:bg-muted"
                                )}
                                data-testid={`unit-conf-${item.prodotto_id}`}
                              >
                                {conf.conf_tipo} ({conf.conf_desc})
                              </button>
                              <button
                                onClick={() => setUnitaOrdine(prev => ({ ...prev, [item.prodotto_id]: item.unita_misura }))}
                                className={cn(
                                  "px-3 py-1.5 rounded-full text-xs font-medium transition-colors",
                                  unit === item.unita_misura
                                    ? "bg-blue-600 text-white"
                                    : "bg-muted/50 text-muted-foreground hover:bg-muted"
                                )}
                                data-testid={`unit-base-${item.prodotto_id}`}
                              >
                                {item.unita_misura}
                              </button>
                            </>
                          ) : (
                            <>
                              {/* Se NON conosco il confezionamento: opzioni filtrate per tipo */}
                              {(() => {
                                const um = (item.unita_misura || "pz").toLowerCase();
                                let packUnits = ["cartoni", "casse"];
                                if (um === "lt" || um === "l" || um === "litro" || um === "litri") packUnits = ["bottiglie", "casse"];
                                if (um === "pz") packUnits = ["cartoni", "buste", "casse"];
                                const baseUnit = (um === "lt" || um === "l" || um === "litro" || um === "litri") ? "lt" : (um === "kg" || um === "g") ? "kg" : "pz";
                                const allUnits = [...new Set([...packUnits, baseUnit, "pz"])];
                                return allUnits.map(u => (
                                  <button
                                    key={u}
                                    onClick={() => setUnitaOrdine(prev => ({ ...prev, [item.prodotto_id]: u }))}
                                    className={cn(
                                      "px-2.5 py-1.5 rounded-full text-xs font-medium transition-colors",
                                      unit === u
                                        ? "bg-blue-600 text-white"
                                        : "bg-muted/50 text-muted-foreground hover:bg-muted"
                                    )}
                                    data-testid={`unit-${u}-${item.prodotto_id}`}
                                  >
                                    {u.charAt(0).toUpperCase() + u.slice(1)}
                                  </button>
                                ));
                              })()}
                            </>
                          )}
                        </div>

                        {/* Riga 4: Info conversione (se conf noto) */}
                        {kgCalc && (
                          <p className="text-xs text-blue-500 font-medium">= {kgCalc} {conf.conf_um}</p>
                        )}

                        {/* Note + cambio fornitore compatto */}
                        {item.note && <p className="text-xs text-muted-foreground italic">Nota: {item.note}</p>}
                        <button
                          onClick={() => setShowFornitorePer(prev => ({ ...prev, [item.prodotto_id]: !prev[item.prodotto_id] }))}
                          className="text-[11px] text-muted-foreground/60 hover:text-muted-foreground transition-colors"
                          data-testid={`toggle-fornitore-${item.prodotto_id}`}
                        >
                          {showFornitorePer[item.prodotto_id] ? "Nascondi fornitore" : "Cambia fornitore"}
                        </button>
                        {showFornitorePer[item.prodotto_id] && (
                          <select
                            className="h-9 w-full text-xs rounded-lg border border-border bg-card px-2"
                            value={fornitoreMod[item.prodotto_id]?.fornitore_id || gruppo.fornitore_id || ""}
                            onChange={e => {
                              const f = fornitori.find(f => f.id === e.target.value);
                              if (f) setFornitoriMod(prev => ({ ...prev, [item.prodotto_id]: { fornitore_id: f.id, fornitore_nome: f.nome } }));
                            }}
                            data-testid={`fornitore-select-${item.prodotto_id}`}
                          >
                            {gruppo.fornitore_id && <option value={gruppo.fornitore_id}>{gruppo.fornitore_nome}</option>}
                            {fornitori.filter(f => f.id !== gruppo.fornitore_id).map(f => (
                              <option key={f.id} value={f.id}>{f.nome}</option>
                            ))}
                          </select>
                        )}
                      </div>
                    );
                  })}

                  {/* Items aggiunti manualmente */}
                  {itemsManuali.map(item => {
                    const unit = unitaOrdine[item.prodotto_id] || item.unita_misura;
                    const unitLabel = unit;
                    return (
                      <div key={item.prodotto_id} className="p-3 rounded-xl bg-emerald-500/5 border border-emerald-500/20 space-y-2" data-testid={`item-manual-${item.prodotto_id}`}>
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-sm text-foreground">{item.prodotto_nome}</span>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setItemsManuali(prev => prev.filter(i => i.prodotto_id !== item.prodotto_id))}>
                            <X className="h-3.5 w-3.5 text-muted-foreground" />
                          </Button>
                        </div>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number" step="any" inputMode="decimal"
                            placeholder="Qtà"
                            value={quantita[item.prodotto_id] || ""}
                            onChange={e => setQuantita(prev => ({ ...prev, [item.prodotto_id]: e.target.value }))}
                            className="h-10 text-base flex-1 font-medium"
                          />
                          <span className="text-sm font-medium text-muted-foreground min-w-[50px] text-center">{unitLabel}</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {(() => {
                            const um = (item.unita_misura || "pz").toLowerCase();
                            let packUnits = ["cartoni", "casse"];
                            if (um === "lt" || um === "l" || um === "litro" || um === "litri") packUnits = ["bottiglie", "casse"];
                            if (um === "pz") packUnits = ["cartoni", "buste", "casse"];
                            const baseUnit = (um === "lt" || um === "l" || um === "litro" || um === "litri") ? "lt" : (um === "kg" || um === "g") ? "kg" : "pz";
                            const allUnits = [...new Set([...packUnits, baseUnit, "pz"])];
                            return allUnits.map(u => (
                              <button
                                key={u}
                                onClick={() => setUnitaOrdine(prev => ({ ...prev, [item.prodotto_id]: u }))}
                                className={cn(
                                  "px-2.5 py-1.5 rounded-full text-xs font-medium transition-colors",
                                  unit === u ? "bg-blue-600 text-white" : "bg-muted/50 text-muted-foreground hover:bg-muted"
                                )}
                              >
                                {u.charAt(0).toUpperCase() + u.slice(1)}
                              </button>
                            ));
                          })()}
                        </div>
                      </div>
                    );
                  })}

                  {/* Pulsanti azione */}
                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" size="sm" onClick={() => setAddDialog(true)} data-testid="add-product-btn">
                      <Plus className="h-3.5 w-3.5 mr-1.5" />Aggiungi prodotto
                    </Button>
                    <Button
                      size="sm" disabled={saving}
                      onClick={() => salvaOrdine(gruppo)}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid={`salva-ordine-${gruppo.fornitore_id}`}
                    >
                      <Save className="h-3.5 w-3.5 mr-1.5" />{saving ? "Salvataggio..." : "Salva ordine"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
            </div>
          )}
        </div>
      )}

      {/* TAB: Storico */}
      {tab === "storico" && (
        <div className="space-y-3">
          {/* Toggle vista: per fornitore / per prodotto */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setStoricoView("fornitore")}
              data-testid="storico-view-fornitore"
              className={cn(
                "h-9 px-4 rounded-xl text-sm font-semibold flex items-center gap-1.5 transition-all",
                storicoView === "fornitore" ? "bg-primary text-white shadow-sm" : "bg-muted text-muted-foreground border border-border hover:bg-muted/80"
              )}
            >
              <Truck className="h-3.5 w-3.5" />Per fornitore
            </button>
            <button
              onClick={() => setStoricoView("prodotto")}
              data-testid="storico-view-prodotto"
              className={cn(
                "h-9 px-4 rounded-xl text-sm font-semibold flex items-center gap-1.5 transition-all",
                storicoView === "prodotto" ? "bg-primary text-white shadow-sm" : "bg-muted text-muted-foreground border border-border hover:bg-muted/80"
              )}
            >
              <Package className="h-3.5 w-3.5" />Per prodotto
            </button>
          </div>

          {/* Filtro periodo */}
          <div className="flex flex-wrap items-center gap-1.5">
            {[
              { k: "mese", l: "Ultimo mese" },
              { k: "3mesi", l: "3 mesi" },
              { k: "6mesi", l: "6 mesi" },
              { k: "anno", l: "Anno" },
              { k: "custom", l: "Personalizzato" },
            ].map(o => (
              <button
                key={o.k}
                onClick={() => setPeriodo(o.k)}
                data-testid={`periodo-${o.k}`}
                className={cn(
                  "h-8 px-3 rounded-lg text-xs font-semibold transition-all",
                  periodo === o.k ? "bg-primary text-white border border-primary shadow-sm" : "bg-muted text-muted-foreground border border-border hover:bg-muted/80"
                )}
              >
                {o.l}
              </button>
            ))}
          </div>
          {periodo === "custom" && (
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <p className="text-[11px] text-muted-foreground mb-1">Da</p>
                <DateInput value={dataDa} onChange={setDataDa} />
              </div>
              <div className="flex-1">
                <p className="text-[11px] text-muted-foreground mb-1">A</p>
                <DateInput value={dataA} onChange={setDataA} />
              </div>
            </div>
          )}

          {ordiniCompletati.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 bg-muted/30 rounded-2xl border border-border">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
                <History className="h-8 w-8 text-muted-foreground/50" />
              </div>
              <p className="text-muted-foreground text-sm">Nessun ordine completato nel periodo</p>
            </div>
          ) : storicoView === "fornitore" ? (
            /* === VISTA PER FORNITORE (raggruppata + frequenza) === */
            <div className="space-y-3">
              {fornitoriAggregati.map(g => {
                const fkey = g.fornitore_id || "senza";
                const isOpen = fornitoreApertoStorico === fkey;
                return (
                  <div key={fkey} className="bg-card rounded-2xl border border-border overflow-hidden" data-testid={`storico-fornitore-${fkey}`}>
                    <div className="p-4 cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => setFornitoreApertoStorico(isOpen ? null : fkey)}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-9 h-9 bg-primary/10 rounded-xl flex items-center justify-center shrink-0"><Truck className="h-4 w-4 text-primary" /></div>
                          <div className="min-w-0">
                            <span className="font-bold text-sm text-foreground truncate block">{g.fornitore_nome}</span>
                            <span className="text-[11px] text-muted-foreground">
                              {g.ordini.length} {g.ordini.length === 1 ? "ordine" : "ordini"}{g.frequenzaGiorni ? ` · ~ogni ${g.frequenzaGiorni} giorni` : ""}
                            </span>
                          </div>
                        </div>
                        {isOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                      </div>
                    </div>
                    {isOpen && (
                      <div className="px-4 pb-4 space-y-2 border-t border-border pt-2">
                        {g.ordini.map(ordine => (
                          <div key={ordine.id} className="rounded-xl bg-muted/30 border border-border p-3" data-testid={`storico-ordine-${ordine.id}`}>
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-1.5">
                                <span className="text-sm font-bold text-foreground">{formatDate(ordine.ordinato_at || ordine.created_at)}</span>
                                <button
                                  onClick={() => setEditDataOrdineId(editDataOrdineId === ordine.id ? null : ordine.id)}
                                  className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                                  data-testid={`edit-data-ordine-${ordine.id}`}
                                >
                                  <Edit3 className="h-3 w-3" />
                                </button>
                              </div>
                              <button onClick={() => rigeneraMessaggio(ordine)} className="text-xs text-primary hover:underline flex items-center gap-1" data-testid={`copia-msg-${ordine.id}`}>
                                <ClipboardCopy className="h-3 w-3" />Copia
                              </button>
                            </div>
                            {editDataOrdineId === ordine.id && (
                              <div className="mb-2">
                                <DateInput
                                  value={(ordine.ordinato_at || ordine.created_at || "").slice(0, 10)}
                                  onChange={(d) => aggiornaDataOrdine(ordine.id, d)}
                                />
                              </div>
                            )}
                            {ordine.items?.map((item, idx) => renderStoricoItem(ordine, item, idx, false))}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            /* === VISTA PER PRODOTTO (totale UdM del periodo + azioni) === */
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Cerca prodotto..." value={storicoSearch} onChange={e => setStoricoSearch(e.target.value)} className="pl-9" data-testid="storico-prodotto-search" />
              </div>
              {(() => {
                const filtrati = prodottiAggregati.filter(p =>
                  !storicoSearch || (p.prodotto_nome || "").toLowerCase().includes(storicoSearch.toLowerCase())
                );
                if (filtrati.length === 0) {
                  return (
                    <div className="flex flex-col items-center justify-center py-16 bg-muted/30 rounded-2xl border border-border">
                      <Package className="h-8 w-8 text-muted-foreground/50 mb-3" />
                      <p className="text-muted-foreground text-sm">Nessun prodotto trovato</p>
                    </div>
                  );
                }
                return filtrati.map(p => (
                  <div key={p.prodotto_id} className="bg-card rounded-2xl border border-border overflow-hidden" data-testid={`storico-prodotto-${p.prodotto_id}`}>
                    <div className="p-4 cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => setProdottoAperto(prodottoAperto === p.prodotto_id ? null : p.prodotto_id)}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <Package className="h-4 w-4 text-primary shrink-0" />
                          <div className="min-w-0">
                            <span className="font-bold text-sm text-foreground truncate block">{p.prodotto_nome}</span>
                            <span className="text-[11px] text-muted-foreground">{p.entries.length} {p.entries.length === 1 ? "ordine" : "ordini"} nel periodo</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-sm font-bold text-primary" data-testid={`totale-${p.prodotto_id}`}>{formatTotali(p.totali)}</span>
                          {prodottoAperto === p.prodotto_id ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                        </div>
                      </div>
                    </div>
                    {prodottoAperto === p.prodotto_id && (
                      <div className="px-4 pb-4 space-y-1 border-t border-border pt-2">
                        {(() => {
                          const mensili = buildMensili(p.entries);
                          if (mensili.length < 2) return null;
                          const max = Math.max(...mensili.map(m => m.val)) || 1;
                          return (
                            <div className="mb-3 p-3 rounded-xl bg-muted/30 border border-border" data-testid={`grafico-${p.prodotto_id}`}>
                              <p className="text-[11px] text-muted-foreground mb-2 font-medium">Andamento acquisti per mese</p>
                              <div className="flex items-end gap-1.5" style={{ height: 72 }}>
                                {mensili.map(m => (
                                  <div key={m.mese} className="flex-1 flex flex-col justify-end items-center h-full min-w-0">
                                    <span className="text-[9px] text-muted-foreground font-semibold leading-none mb-0.5">{m.val}</span>
                                    <div
                                      className="w-full rounded-t bg-primary/70 hover:bg-primary transition-colors"
                                      style={{ height: `${Math.max(6, (m.val / max) * 85)}%` }}
                                      title={`${m.val}`}
                                    />
                                  </div>
                                ))}
                              </div>
                              <div className="flex gap-1.5 mt-1">
                                {mensili.map(m => {
                                  const [y, mm] = m.mese.split("-");
                                  return <span key={m.mese} className="flex-1 text-center text-[9px] text-muted-foreground truncate">{MESI_SHORT[parseInt(mm) - 1]} '{y.slice(2)}</span>;
                                })}
                              </div>
                            </div>
                          );
                        })()}
                        {p.entries.map((entry, idx) => renderStoricoItem(entry.ordine, entry.item, idx, true))}
                      </div>
                    )}
                  </div>
                ));
              })()}
            </div>
          )}
        </div>
      )}

      {/* Dialog: Aggiungi prodotto */}
      <AggiungiProdottoDialog
        open={addDialog}
        onOpenChange={setAddDialog}
        addSearch={addSearch}
        onSearch={searchProdotti}
        addResults={addResults}
        onAdd={aggiungiProdottoManuale}
      />

      {/* Dialog: Messaggio generato */}
      <MessaggioDialog
        open={messaggioDialog}
        onOpenChange={setMessaggioDialog}
        messaggioGenerato={messaggioGenerato}
        onCopia={() => { copiaMessaggio(messaggioGenerato); setMessaggioDialog(false); }}
      />

      {/* Dialog: Template messaggio */}
      <TemplateDialog
        open={templateDialog}
        onOpenChange={setTemplateDialog}
        template={template}
        setTemplate={setTemplate}
        onSave={salvaTemplate}
      />

      {/* ===== OVERLAY: Nuovo Ordine Manuale ===== */}
      {nuovoOrdineStep && (
        <div className="fixed inset-0 z-50 bg-background" data-testid="nuovo-ordine-overlay">
          {/* STEP 1: Seleziona prodotti */}
          {nuovoOrdineStep === "seleziona" && (
            <div className="flex flex-col h-full">
              <div className="sticky top-0 z-10 bg-card border-b border-border px-4 py-3 shadow-sm">
                <div className="flex items-center gap-3">
                  <Button variant="ghost" size="icon" onClick={() => setNuovoOrdineStep(null)} data-testid="chiudi-nuovo-ordine">
                    <ArrowLeft className="h-5 w-5" />
                  </Button>
                  <div className="flex-1">
                    <h2 className="font-bold">Nuovo Ordine</h2>
                    <p className="text-xs text-muted-foreground">Seleziona i prodotti da ordinare</p>
                  </div>
                  {prodottiSelezionati.length > 0 && (
                    <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white font-bold" onClick={passaAQuantita} data-testid="btn-prosegui-qty">
                      Avanti ({prodottiSelezionati.length})
                    </Button>
                  )}
                </div>
                <div className="relative mt-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Cerca prodotto..."
                    value={prodottiSearch}
                    onChange={e => setProdottiSearch(e.target.value)}
                    className="pl-9"
                    data-testid="search-prodotti-nuovo"
                  />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-1">
                {loadingProdotti ? (
                  <p className="text-center text-muted-foreground py-8">Caricamento...</p>
                ) : prodottiFiltrati.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">Nessun prodotto trovato</p>
                ) : prodottiFiltrati.map(p => {
                  const selected = prodottiSelezionati.some(x => x.id === p.id);
                  return (
                    <button
                      key={p.id}
                      onClick={() => toggleProdottoSelezionato(p)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all text-left ${
                        selected
                          ? 'bg-green-600/10 border-green-600/30'
                          : 'bg-background border-border hover:border-primary/30'
                      }`}
                      data-testid={`prodotto-${p.id}`}
                    >
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                        selected ? 'bg-green-600 border-green-600' : 'border-muted-foreground/40'
                      }`}>
                        {selected && <Check className="h-3 w-3 text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{p.nome}</p>
                        <p className="text-[10px] text-muted-foreground">{p.fornitore_nome || "Nessun fornitore"}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
              {/* Footer fisso con conteggio */}
              {prodottiSelezionati.length > 0 && (
                <div className="sticky bottom-0 bg-background border-t border-border p-4">
                  <Button className="w-full bg-green-600 hover:bg-green-700 text-white font-bold" onClick={passaAQuantita} data-testid="btn-prosegui-qty-bottom">
                    Prosegui con {prodottiSelezionati.length} prodotti
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* STEP 2: Quantità */}
          {nuovoOrdineStep === "quantita" && (
            <div className="flex flex-col h-full">
              <div className="sticky top-0 z-10 bg-card border-b border-border px-4 py-3 shadow-sm">
                <div className="flex items-center gap-3">
                  <Button variant="ghost" size="icon" onClick={() => setNuovoOrdineStep("seleziona")} data-testid="btn-torna-seleziona">
                    <ArrowLeft className="h-5 w-5" />
                  </Button>
                  <div className="flex-1">
                    <h2 className="font-bold">Quantitativi</h2>
                    <p className="text-xs text-muted-foreground">{prodottiSelezionati.length} prodotti selezionati</p>
                  </div>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {prodottiSelezionati.map(p => {
                  const conf = p.confezionamento;
                  const um = (p.unita_misura || "pz").toLowerCase();
                  const selectedUnit = unitaOrdine[p.id] || (conf ? "conf" : um);
                  
                  // Unità intelligenti basate su unita_misura del prodotto
                  let units = [];
                  if (conf) {
                    units.push({ key: "conf", label: conf.conf_desc || `${conf.conf_tipo} ${conf.conf_qtà_unitaria}${conf.conf_um}` });
                  }
                  // Packaging specifico in base al tipo di prodotto
                  if (um === "lt" || um === "l" || um === "litro" || um === "litri") {
                    units.push({ key: "bottiglie", label: "Bottiglie" });
                    units.push({ key: "casse", label: "Casse" });
                    units.push({ key: "lt", label: "lt" });
                    units.push({ key: "pz", label: "pz" });
                  } else if (um === "pz") {
                    units.push({ key: "cartoni", label: "Cartoni" });
                    units.push({ key: "buste", label: "Buste" });
                    units.push({ key: "casse", label: "Casse" });
                    units.push({ key: "pz", label: "pz" });
                  } else {
                    // kg products (default)
                    units.push({ key: "cartoni", label: "Cartoni" });
                    units.push({ key: "casse", label: "Casse" });
                    units.push({ key: "kg", label: "kg" });
                    units.push({ key: "pz", label: "pz" });
                  }
                  
                  return (
                    <div key={p.id} className="p-3 bg-card rounded-lg border border-border space-y-2" data-testid={`qty-card-${p.id}`}>
                      <div className="flex items-center justify-between">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-bold truncate">{p.nome}</p>
                          <div className="flex items-center gap-1 mt-1">
                            <span className="text-[10px] text-muted-foreground shrink-0">Da:</span>
                            <select
                              value={p.id in fornitoreMod ? (fornitoreMod[p.id].fornitore_id || "") : (p.fornitore_id || "")}
                              onChange={e => {
                                const f = fornitori.find(x => x.id === e.target.value);
                                setFornitoriMod(prev => ({
                                  ...prev,
                                  [p.id]: f
                                    ? { fornitore_id: f.id, fornitore_nome: f.nome }
                                    : { fornitore_id: null, fornitore_nome: "Senza fornitore" },
                                }));
                              }}
                              className="text-[10px] bg-background border border-border rounded px-1 py-0.5 max-w-[180px] truncate"
                              data-testid={`nuovo-ordine-fornitore-${p.id}`}
                            >
                              <option value="">Senza fornitore</option>
                              {fornitori.map(f => (
                                <option key={f.id} value={f.id}>{f.nome}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400" onClick={() => setProdottiSelezionati(prev => prev.filter(x => x.id !== p.id))}>
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                      {/* Input quantità + pillole unità */}
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          inputMode="decimal"
                          value={quantita[p.id] || ""}
                          onChange={e => setQuantita(prev => ({ ...prev, [p.id]: e.target.value }))}
                          placeholder="Qtà"
                          className="w-16 h-9 text-center text-sm flex-shrink-0 [&::-webkit-inner-spin-button]:appearance-none"
                          data-testid={`qty-input-${p.id}`}
                        />
                        <div className="flex flex-wrap gap-1 flex-1">
                          {units.map(u => (
                            <button
                              key={u.key}
                              onClick={() => setUnitaOrdine(prev => ({ ...prev, [p.id]: u.key }))}
                              className={`px-2 py-1 rounded-md text-[10px] font-medium border transition-all ${
                                selectedUnit === u.key
                                  ? 'bg-primary text-primary-foreground border-primary'
                                  : 'bg-background border-border text-muted-foreground'
                              }`}
                            >
                              {u.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Footer salva */}
              <div className="sticky bottom-0 bg-background border-t border-border p-4">
                <Button
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-bold"
                  onClick={salvaOrdineManuale}
                  disabled={saving}
                  data-testid="btn-salva-ordine-manuale"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? "Salvataggio..." : "Salva Ordine"}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OrdiniFornitori;
